const DEFAULT_STORAGE_KEY = 'cart_delivery_postcode';

function isCoarseTouchDevice() {
  return (
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(hover: none) and (pointer: coarse)').matches
  );
}

function debouncePostcode(fn, wait) {
  let t;
  return function debounced(...args) {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}

/**
 * Formats a shipping rate price using the shop money-with-currency pattern (same idea as shipping-calculator.js).
 * @param {string} amount - Amount string from the Cart API (e.g. "12.00").
 * @returns {string}
 */
function formatShippingPrice(amount) {
  const fmt =
    (window.theme && theme.settings && theme.settings.moneyFormat) ||
    (window.theme && theme.settings && theme.settings.moneyWithCurrencyFormat);
  if (!fmt) return amount;
  return fmt.replace(/\{\{\s*(\w+)\s*\}\}/, amount);
}

/**
 * Returns the cheapest shipping rate object from the API response, preferring the lowest positive price.
 * @param {Array<{ price: string, name?: string }>} rates
 * @returns {{ price: string, name: string, n: number } | null}
 */
function getCheapestRate(rates) {
  if (!rates || !rates.length) return null;
  const parsed = rates.map((r) => ({ ...r, n: parseFloat(String(r.price), 10) })).filter((r) => !isNaN(r.n));
  if (!parsed.length) return null;
  const globalMin = Math.min(...parsed.map((r) => r.n));
  const pool = globalMin <= 0 && parsed.some((r) => r.n > 0) ? parsed.filter((r) => r.n > 0) : parsed;
  return pool.reduce((best, r) => (r.n < best.n ? r : best), pool[0]);
}

/** Money string from integer cents (matches cart drawer / postcode formatters). */
function ccFormatMoneyCents(cents) {
  const fmt =
    (window.theme && theme.settings && theme.settings.moneyFormat) ||
    (window.theme && theme.settings && theme.settings.moneyWithCurrencyFormat);
  const n = (Number(cents) || 0) / 100;
  const amt = n.toFixed(2);
  if (!fmt) return amt;
  return String(fmt).replace(/\{\{\s*(\w+)\s*\}\}/, amt);
}

if (!customElements.get('cart-postcode-checker')) {
  class CartPostcodeChecker extends HTMLElement {
    connectedCallback() {
      /** Logged-in customer: editable field prefilled from account; no guest localStorage. */
      this.customer = this.dataset.customer === 'true';
      this.form = this.querySelector('.cart-postcode-checker__form');
      this.input = this.querySelector('[data-postcode-input]');
      this.display = this.querySelector('[data-postcode-display]');
      this.ratesEl = this.querySelector('[data-postcode-rates]');
      this.errorEl = this.querySelector('[data-postcode-error]');
      this.submitBtn = this.querySelector('[data-postcode-submit]');
      this.storageKey = this.dataset.storageKey || DEFAULT_STORAGE_KEY;
      this.defaultCountry = (this.dataset.defaultCountry || 'US').trim();
      this.defaultProvince = (this.dataset.defaultProvince || '').trim();

      this.summaryLine = this.querySelector('[data-postcode-summary-line]');
      this.fieldLine = this.querySelector('[data-postcode-field-line]');
      this.summaryValue = this.querySelector('[data-postcode-summary-value]');
      this.editBtn = this.querySelector('[data-postcode-edit]');
      const container = this.closest('[data-cc-postcode-date-container]');
      this.deliveryHint = container ? container.querySelector('[data-cc-delivery-hint]') : null;
      this.postcodeEditing = false;

      this.boundEdit = this.boundEdit || this.handleEditClick.bind(this);
      if (this.editBtn) {
        this.editBtn.addEventListener('click', this.boundEdit);
      }

      this.boundSubmit = this.boundSubmit || this.handleSubmit.bind(this);
      if (this.form && this.form.tagName === 'FORM') {
        this.form.addEventListener('submit', this.boundSubmit);
      } else if (this.submitBtn) {
        // Wrapper is a <div> (cart page: avoid nesting inside the page-level cart form).
        // Also handle Enter key on the input so it behaves like a real form.
        this.submitBtn.addEventListener('click', this.boundSubmit);
        this.boundInputEnter = this.boundInputEnter || function (e) {
          if (e.key === 'Enter') this.boundSubmit(e);
        }.bind(this);
        if (this.input) this.input.addEventListener('keydown', this.boundInputEnter);
      }

      this.boundInputClearNudge = this.boundInputClearNudge || this.clearPostcodeEmptyNudge.bind(this);
      if (this.input) {
        this.applyPostcodeInputMobileNoZoom();
        if (this.input.dataset.placeholderDefault == null) {
          this.input.dataset.placeholderDefault = this.input.getAttribute('placeholder') || '';
        }
        this.input.addEventListener('input', this.boundInputClearNudge);
      }

      this.boundCartChange =
        this.boundCartChange ||
        debouncePostcode((ev) => {
          const zip = this.getPostcodeForRates();
          if (!zip) return;

          const detail = ev && ev.detail;

          // Delivery date changes don't affect postcode or shipping eligibility —
          // the picker calls syncCartDrawerCheckoutGate directly and handles its own cascade.
          if (detail && detail.source === 'delivery-date-picker') {
            this.syncCompactUi();
            return;
          }

          const fp = detail && detail.cartFingerprint;
          const lastFp =
            typeof window.ccGetLastCartFingerprint === 'function'
              ? window.ccGetLastCartFingerprint()
              : '';

          if (
            fp &&
            fp === lastFp &&
            window.ccPostcodeShippingRatesValid === true
          ) {
            this.syncCompactUi();
            return;
          }

          this.fetchShippingForPostcode(zip, { silent: true });
        }, 400);
      document.addEventListener('on:cart:change', this.boundCartChange);

      if (this.customer) {
        const initial = (this.dataset.initialPostcode || '').trim();
        if (this.input && !this.input.value.trim() && initial) {
          this.input.value = initial;
        }
        const zip = this.getPostcodeForRates();
        this.renderPostcodeLine(zip);
        if (zip) {
          // Optimistically restore valid flag so the checkout button isn't disabled during the async re-fetch.
          // fetchShippingForPostcode will correct it if the postcode is no longer serviceable.
          try { if (sessionStorage.getItem('cc_postcode_rates_valid')) window.ccPostcodeShippingRatesValid = true; } catch (e) {}
          this.fetchShippingForPostcode(zip, { silent: true });
        } else {
          this.hideRatesAndError();
        }
        this.syncCompactUi();
      } else {
        const stored = this.readStored().trim();
        if (this.input) {
          this.input.value = stored || '';
        }
        const zip = this.getPostcodeForRates();
        this.renderPostcodeLine(zip);
        if (zip) {
          // Optimistically restore valid flag so the checkout button isn't disabled during the async re-fetch.
          // fetchShippingForPostcode will correct it if the postcode is no longer serviceable.
          try { if (sessionStorage.getItem('cc_postcode_rates_valid')) window.ccPostcodeShippingRatesValid = true; } catch (e) {}
          this.fetchShippingForPostcode(zip, { silent: true });
        } else {
          this.hideRatesAndError();
        }
        this.syncCompactUi();
      }
    }

    disconnectedCallback() {
      if (this.form && this.form.tagName === 'FORM' && this.boundSubmit) {
        this.form.removeEventListener('submit', this.boundSubmit);
      } else if (this.submitBtn && this.boundSubmit) {
        this.submitBtn.removeEventListener('click', this.boundSubmit);
        if (this.input && this.boundInputEnter) {
          this.input.removeEventListener('keydown', this.boundInputEnter);
        }
      }
      if (this.editBtn && this.boundEdit) {
        this.editBtn.removeEventListener('click', this.boundEdit);
      }
      if (this.boundCartChange) {
        document.removeEventListener('on:cart:change', this.boundCartChange);
      }
      if (this.input && this.boundInputClearNudge) {
        this.input.removeEventListener('input', this.boundInputClearNudge);
      }
    }

    /**
     * Postcode used for shipping_rates.json (guest: input only; customer: input, else account default from data attribute).
     * @returns {string}
     */
    getPostcodeForRates() {
      const typed = (this.input && this.input.value.trim()) || '';
      if (this.customer) {
        return typed || (this.dataset.initialPostcode || '').trim();
      }
      return typed;
    }

    readStored() {
      if (this.customer) return '';
      try {
        return localStorage.getItem(this.storageKey) || '';
      } catch {
        return '';
      }
    }

    writeStored(value) {
      if (this.customer) return;
      try {
        if (value) {
          localStorage.setItem(this.storageKey, value);
        } else {
          localStorage.removeItem(this.storageKey);
        }
      } catch {
        /* ignore quota / private mode */
      }
    }

    strings() {
      return window.theme && theme.strings ? theme.strings : {};
    }

    setLoading(isLoading, silent) {
      if (this.submitBtn && !silent) {
        this.submitBtn.disabled = isLoading;
        this.submitBtn.classList.toggle('is-loading', isLoading);
      }
      if (isLoading && !silent && this.ratesEl) {
        const s = this.strings();
        this.ratesEl.textContent = s.postcodeLoading || '';
        this.ratesEl.classList.remove('hidden');
        if (this.errorEl) this.errorEl.classList.add('hidden');
      }
    }

    hideRatesAndError() {
      window.ccPostcodeShippingRatesValid = false;
      window.ccPostcodeRatesErrorMessage = '';
      try { sessionStorage.removeItem('cc_postcode_rates_valid'); } catch (e) {}
      if (this.ratesEl) {
        this.ratesEl.textContent = '';
        this.ratesEl.classList.add('hidden');
      }
      if (this.errorEl) {
        this.errorEl.textContent = '';
        this.errorEl.classList.add('hidden');
      }
      if (this.deliveryHint) {
        this.deliveryHint.textContent = this.deliveryHint.dataset.defaultHint || '';
        this.deliveryHint.classList.remove('cc-delivery-hint--has-rate');
      }
    }

    showError(message) {
      this.postcodeEditing = true;
      window.ccPostcodeShippingRatesValid = false;
      try { sessionStorage.removeItem('cc_postcode_rates_valid'); } catch (e) {}
      window.ccPostcodeRatesErrorMessage = String(message || '').trim();
      if (!this.errorEl) {
        if (window.syncCartDrawerCheckoutGate) {
          window.syncCartDrawerCheckoutGate();
        }
        return;
      }
      this.errorEl.textContent = message;
      this.errorEl.classList.remove('hidden');
      if (this.ratesEl) this.ratesEl.classList.add('hidden');
      this.syncCompactUi();
    }

    /**
     * @param {object} data - Parsed shipping_rates.json body.
     * @param {{ silent?: boolean }} [opts]
     */
    showRates(data, opts = {}) {
      const { silent } = opts;
      const s = this.strings();
      const sc = s.shippingCalculator || {};

      if (!this.ratesEl) return;
      this.ratesEl.textContent = '';

      if (data.shipping_rates && data.shipping_rates.length) {
        window.ccPostcodeShippingRatesValid = true;
        window.ccPostcodeRatesErrorMessage = '';
        try { sessionStorage.setItem('cc_postcode_rates_valid', '1'); } catch (e) {}

        const headingKey = data.shipping_rates.length === 1 ? 'singleRate' : 'multipleRates';
        const heading = sc[headingKey] || '';

        const wrap = document.createElement('div');
        const h = document.createElement('p');
        h.className = 'mb-1 font-medium';
        h.textContent = heading;
        wrap.appendChild(h);

        const ul = document.createElement('ul');
        ul.className = 'styled-list mb-0';
        const seen = new Set();
        data.shipping_rates.forEach((rate) => {
          const priceNum = parseFloat(String(rate.price), 10);
          const priceLabel =
            !Number.isNaN(priceNum) && priceNum <= 0
              ? s.postcodeRateFree || 'Free'
              : formatShippingPrice(String(rate.price));
          const key = `${rate.name}|${priceLabel}`;
          if (seen.has(key)) return;
          seen.add(key);
          const li = document.createElement('li');
          li.textContent = `${rate.name}: ${priceLabel}`;
          ul.appendChild(li);
        });
        wrap.appendChild(ul);

        this.ratesEl.appendChild(wrap);
        this.ratesEl.classList.remove('hidden');
        if (this.errorEl) this.errorEl.classList.add('hidden');

        if (this.deliveryHint) {
          const cheapest = getCheapestRate(data.shipping_rates);
          const surcharge = (this.deliveryHint.dataset.surcharge || '').trim();
          if (cheapest) {
            const priceLabel = cheapest.n <= 0
              ? (s.postcodeRateFree || 'Free')
              : formatShippingPrice(String(cheapest.price));
            const escapedName = (cheapest.name || 'Delivery').trim().replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;');
            const escapedPrice = priceLabel.replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;');
            const escapedSurcharge = surcharge.replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;');
            const surchargePart = surcharge ? '  |  *<strong>' + escapedSurcharge + '</strong> extra for weekend delivery.' : '';
            this.deliveryHint.innerHTML = escapedName + ': <strong>' + escapedPrice + '</strong>' + surchargePart;
            this.deliveryHint.classList.add('cc-delivery-hint--has-rate');
          }
        }
      } else {
        window.ccPostcodeShippingRatesValid = false;
        const msg = (s.postcodeNoRates || sc.noRates || s.postcodeFetchError || '').trim();
        window.ccPostcodeRatesErrorMessage = msg;
        if (this.errorEl) {
          if (silent) {
            this.errorEl.textContent = '';
            this.errorEl.classList.add('hidden');
          } else {
            this.errorEl.textContent = msg;
            this.errorEl.classList.remove('hidden');
            this.postcodeEditing = true;
          }
        }
        this.ratesEl.classList.add('hidden');
      }
    }

    /**
     * Parses error payload from shipping_rates.json (object of field -> messages).
     * @param {object} data
     * @returns {string}
     */
    formatApiErrors(data) {
      if (!data || typeof data !== 'object') return '';
      const parts = [];
      Object.keys(data).forEach((key) => {
        const val = data[key];
        if (Array.isArray(val)) {
          val.forEach((m) => parts.push(String(m)));
        } else if (val) {
          parts.push(String(val));
        }
      });
      return parts.join(' ');
    }

    async cartHasItems() {
      try {
        const cart =
          typeof window.ccGetCartSnapshot === 'function'
            ? await window.ccGetCartSnapshot()
            : await fetch(`${theme.routes.cart}.js`).then((r) => (r.ok ? r.json() : null));
        if (!cart) return null;
        return (cart.items && cart.items.length > 0) || (cart.item_count && cart.item_count > 0);
      } catch {
        return null;
      }
    }

    /**
     * Fetches shipping rates for the current cart and given postcode (Shopify shipping zones / carrier-calculated rates).
     * @param {string} zip
     * @param {{ silent?: boolean }} [opts]
     */
    async fetchShippingForPostcode(zip, opts = {}) {
      const { silent } = opts;
      const s = this.strings();

      if (!silent) {
        this.hideRatesAndError();
      }
      this.setLoading(true, silent);

      const hasItems = await this.cartHasItems();
      if (hasItems === null) {
        this.setLoading(false, silent);
        this.showError(s.postcodeCartError || '');
        return;
      }
      if (!hasItems) {
        this.setLoading(false, silent);
        this.showError(s.postcodeEmptyCart || '');
        return;
      }

      const params = new URLSearchParams();
      params.set('shipping_address[zip]', zip);
      params.set('shipping_address[country]', this.defaultCountry);
      params.set('shipping_address[province]', this.defaultProvince);

      const url = `${theme.routes.cart}/shipping_rates.json?${params.toString()}`;

      try {
        const response = await fetch(url);
        const data = await response.json();

        if (response.ok) {
          this.showRates(data, { silent });
          if (!silent) {
            this.postcodeEditing = false;
          }
        } else {
          const apiMsg = this.formatApiErrors(data);
          this.showError(apiMsg || s.postcodeFetchError || '');
        }
      } catch {
        this.showError(s.postcodeFetchError || '');
      } finally {
        this.setLoading(false, silent);
        this.syncCompactUi();
        if (window.syncCartDrawerCheckoutGate) {
          window.syncCartDrawerCheckoutGate();
        }
      }
    }

    renderPostcodeLine(value) {
      if (!this.display) return;
      const s = this.strings();
      if (this.customer && !value) {
        this.display.textContent = s.postcodeCustomerMissing || '';
        return;
      }
      if (value) {
        const tpl = s.postcodeChosen || 'Your postcode: __POSTCODE__';
        this.display.textContent = tpl.replace('__POSTCODE__', value);
      } else {
        this.display.textContent = s.postcodeNone || '';
      }
    }

    applyPostcodeInputMobileNoZoom() {
      if (!this.input || !isCoarseTouchDevice()) return;
      this.input.classList.add('cart-postcode-checker__input--mobile-no-zoom');

      // iOS Safari zooms on focus when font-size < 16px.
      // Setting maximum-scale=1 on touchstart (before iOS decides to zoom) prevents it.
      // Restored on blur so the user can still pinch-zoom the rest of the page.
      if (this._iosZoomGuardAttached) return;
      this._iosZoomGuardAttached = true;

      var viewportMeta = document.querySelector('meta[name="viewport"]');
      if (!viewportMeta) return;

      var originalContent = viewportMeta.getAttribute('content') || '';

      this.input.addEventListener('touchstart', function () {
        var content = originalContent.replace(/,?\s*maximum-scale=[^,]*/gi, '').trim();
        viewportMeta.setAttribute('content', content + ', maximum-scale=1');
      }, { passive: true });

      this.input.addEventListener('blur', function () {
        viewportMeta.setAttribute('content', originalContent);
      });
    }

    focusPostcodeInput() {
      if (!this.input) return;
      this.applyPostcodeInputMobileNoZoom();
      try {
        this.input.focus({ preventScroll: true });
      } catch {
        this.input.focus();
      }
    }

    handleEditClick() {
      this.postcodeEditing = true;
      this.syncCompactUi();
      this.focusPostcodeInput();
    }

    clearPostcodeEmptyNudge() {
      if (!this.input) return;
      if (!this.input.value.trim()) return;
      this.input.removeAttribute('aria-invalid');
      this.input.classList.remove('cart-postcode-checker__input--empty-nudge');
      const def = this.input.dataset.placeholderDefault;
      if (def != null) {
        this.input.setAttribute('placeholder', def);
      }
    }

    nudgeEmptyPostcode() {
      const s = this.strings();
      const msg = s.postcodeRequiredForCheck || 'Enter postcode to check.';
      if (this.submitBtn) {
        this.submitBtn.classList.remove('cc-postcode-shake');
        // Reflow so repeated empty submits replay the animation
        void this.submitBtn.offsetWidth;
        this.submitBtn.classList.add('cc-postcode-shake');
        window.setTimeout(() => {
          if (this.submitBtn) this.submitBtn.classList.remove('cc-postcode-shake');
        }, 520);
      }
      if (typeof navigator !== 'undefined' && navigator.vibrate) {
        const reduceMotion =
          window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        if (!reduceMotion) {
          try {
            navigator.vibrate([10, 35, 10, 35, 10]);
          } catch {
            /* ignore */
          }
        }
      }
      if (this.input) {
        if (this.input.dataset.placeholderDefault == null) {
          this.input.dataset.placeholderDefault = this.input.getAttribute('placeholder') || '';
        }
        this.input.setAttribute('aria-invalid', 'true');
        this.input.classList.add('cart-postcode-checker__input--empty-nudge');
        this.input.setAttribute('placeholder', msg);
        this.focusPostcodeInput();
      }
    }

    /**
     * Figma-style twin cells: collapsed row (Postcode : X + Edit) vs field row (input + Check).
     */
    syncCompactUi() {
      if (!this.fieldLine || !this.summaryLine) return;
      const zip = this.getPostcodeForRates();
      const collapse = Boolean(zip) && !this.postcodeEditing;

      if (collapse) {
        this.summaryLine.hidden = false;
        this.fieldLine.hidden = true;
        if (this.summaryValue) {
          this.summaryValue.textContent = zip;
        }
        if (this.display) {
          this.display.classList.add('visually-hidden');
          this.display.setAttribute('aria-hidden', 'true');
        }
      } else {
        this.summaryLine.hidden = true;
        this.fieldLine.hidden = false;
        if (this.display) {
          this.display.classList.remove('visually-hidden');
          this.display.removeAttribute('aria-hidden');
        }
      }
      if (window.syncCartDrawerCheckoutGate) {
        window.syncCartDrawerCheckoutGate();
      }
    }

    async handleSubmit(evt) {
      if (!this.form) return;
      evt.preventDefault();
      const raw = this.input ? this.input.value : '';
      const value = raw.trim();
      if (value) {
        this.clearPostcodeEmptyNudge();
      }
      this.writeStored(value);
      this.renderPostcodeLine(value);

      if (!value) {
        this.hideRatesAndError();
        this.postcodeEditing = true;
        this.nudgeEmptyPostcode();
        this.syncCompactUi();
        return;
      }

      await this.fetchShippingForPostcode(value, { silent: false });
    }
  }

  customElements.define('cart-postcode-checker', CartPostcodeChecker);
}
